/**
 * components/hazard/LocationSection.js — Байршил тодорхойлох хэсэг.
 *
 * Цех / Хэсэг сонголтыг үндсэн DepartmentTseh / DepartmentNegj component-аар
 * (АШУА-ын хамааралтай нэгжүүд) ачаалж буцаана. GPS байршлыг автоматаар авах
 * товчтой.
 *
 * Props:
 *   deptCode, onDeptChange(code, parentId, name)
 *   deptParent
 *   sectionCode, onSectionChange(code, name)
 *   locationText, onLocationTextChange
 *   gps              : { lat, lng, accuracy }
 *   onGpsChange      : (gps) => void
 *   onLocationGuess  : (text) => void  — reverse-geocode result
 */
import React, { useState } from "react";
import DepartmentTseh from "src/components/departmentTseh";
import DepartmentNegj from "src/components/departmentNegj";
import useAccessScope from "src/components/risk/useAccessScope";
import { Card, SectionTitle, inputStyle } from "./common";

export default function LocationSection({
  deptCode,
  onDeptChange,
  deptParent,
  sectionCode,
  onSectionChange,
  locationText = "",
  onLocationTextChange,
  // Гараар бичих байршлын дэлгэрэнгүй тайлбар — GPS-ээс өөр талбар
  locationDescription = "",
  onLocationDescriptionChange,
  gps = { lat: null, lng: null, accuracy: null },
  onGpsChange,
  onLocationGuess,
  // Энгийн ажилтан, ажлын даалгавартай, ӨЭА мөн нар нь ажлынхаа цех/хэсгийг
  // backend-аас аль хэдийн мэдэж байгаа учраас энд нуухаар тохируулсан.
  hideDeptSelectors = false,
}) {
  const [geoStatus, setGeoStatus] = useState("idle");

  // ── Цех / Хэсгийн сонголтыг ЭРХИЙН ТҮВШИНГЭЭР удирдана ──────────────────
  //   factory (ХАБ хэлтэс · sso_owner · бүрэн эрх)
  //        → 2 сонголт хоёул нээлттэй, автомат сонголтгүй → бүгдийг харна
  //   cex    (БН-ийн ХАБ ажилтан / дарга г.м.)
  //        → Цех нь ӨӨРИЙНХӨӨР түгжигдэнэ, Хэсэг нь тухайн цех доторх бүгд
  //   brigade(анхан шатны удирдах ажилтан 168) / self (энгийн ажилтан)
  //        → Цех ба Хэсэг хоёул өөрийнхөөр түгжигдэнэ
  // Түгжигдсэн үед DepartmentTseh/DepartmentNegj өөрсдөө хэрэглэгчийн
  // tseh_code / negj_code-оор автоматаар сонгодог тул утга нь бөглөгдөнө.
  const scope = useAccessScope();
  const lockDept = scope.level !== "factory";
  // brigade (анхан шатны удирдах ажилтан) нь өөрийн хэсэгтээ ажилладаг тул
  // ажилтантай адил хэсгээрээ түгжигдэнэ.
  const lockSection = scope.level === "self" || scope.level === "brigade";

  const capture = () => {
    if (!navigator.geolocation) {
      setGeoStatus("error");
      return;
    }
    setGeoStatus("loading");
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude, longitude, accuracy } = pos.coords;
        onGpsChange?.({ lat: latitude, lng: longitude, accuracy });
        const coordStr = `${latitude.toFixed(5)}, ${longitude.toFixed(5)}`;
        let addr = coordStr;
        try {
          const r = await fetch(
            "https://nominatim.openstreetmap.org/reverse?format=json" +
              `&lat=${latitude}&lon=${longitude}&accept-language=mn`,
          );
          const j = await r.json();
          addr = j?.display_name || coordStr;
        } catch {
          /* reverse geocode амжилтгүй — координат хэвээр */
        }
        onLocationGuess?.(addr);
        if (!locationText) onLocationTextChange?.(addr);
        setGeoStatus("done");
      },
      () => setGeoStatus("error"),
      { enableHighAccuracy: true, timeout: 10000 },
    );
  };

  return (
    <Card>
      <style>{`
        .hz-loc-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 10px;
          margin-bottom: 10px;
        }
        .hz-loc-field { min-width: 0; overflow: hidden; }
        .hz-loc-field .ant-select { width: 100% !important; }
        .hz-loc-field .ant-select-selector {
          max-width: 100% !important;
          overflow: hidden;
        }
        .hz-loc-field .ant-select-selection-item {
          overflow: hidden !important;
          text-overflow: ellipsis !important;
          white-space: nowrap !important;
          padding-right: 18px !important;
        }
        .hz-loc-field-label {
          font-size: 11px;
          font-weight: 700;
          color: #5F5E5A;
          margin-bottom: 4px;
          display: block;
          letter-spacing: 0.3px;
        }
        @media (max-width: 600px) {
          .hz-loc-grid { grid-template-columns: 1fr; gap: 8px; }
          .hz-loc-field .ant-select-selection-item { font-size: 12px !important; }
          .hz-loc-field .ant-select .ant-select-selector { padding: 0 8px !important; }
        }
      `}</style>
      <SectionTitle
        right={
          geoStatus === "loading"
            ? "Авч байна..."
            : geoStatus === "done"
              ? "GPS бэлэн"
              : geoStatus === "error"
                ? "GPS алдаатай"
                : null
        }
      >
        Байршил
      </SectionTitle>

      {hideDeptSelectors ? null : (
        <div className="hz-loc-grid">
          <div className="hz-loc-field">
            <label className="hz-loc-field-label">
              Цех{lockDept ? " · өөрийн" : ""}
            </label>
            <DepartmentTseh
              value={deptCode || undefined}
              noAutoSelect={!lockDept}
              disabled={lockDept}
              onChange={(code, parentId, name) =>
                onDeptChange?.(code || "", parentId || null, name || "")
              }
            />
          </div>
          <div className="hz-loc-field">
            <label className="hz-loc-field-label">
              Хэсэг / нэгж{lockSection ? " · өөрийн" : ""}
            </label>
            <DepartmentNegj
              key={`${deptCode || "x"}-${deptParent || "no-parent"}`}
              value={sectionCode || undefined}
              paranetid={deptParent}
              // parentid зөрүүтэй үед ч кодын угтвараар холбоно (InspectionStartButton-той ижил)
              parentCode={deptCode}
              autoSelectOwn={lockSection}
              disabled={lockSection}
              onChange={(code, name) =>
                onSectionChange?.(code || "", name || "")
              }
            />
          </div>
        </div>
      )}

      <div
        style={{
          display: "flex",
          gap: 8,
          flexWrap: "wrap",
        }}
      >
        <input
          value={locationText}
          onChange={(e) => onLocationTextChange?.(e.target.value)}
          placeholder="GPS-ээр буюу гараар: Жш: Концентратор, 4-р цех"
          style={{
            ...inputStyle,
            flex: "1 1 200px",
            minWidth: 0,
          }}
        />
        <button
          type="button"
          onClick={capture}
          title="GPS-аар байршил авах"
          aria-label="GPS-аар байршил авах"
          disabled={geoStatus === "loading"}
          style={{
            padding: "10px 14px",
            borderRadius: 10,
            border: "1px solid #1B7A45",
            background: "#1B7A45",
            color: "#fff",
            cursor: geoStatus === "loading" ? "wait" : "pointer",
            fontSize: 14,
            fontWeight: 600,
            whiteSpace: "nowrap",
            flexShrink: 0,
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            lineHeight: 1,
            minHeight: 38,
          }}
        >
          {/* SVG icon — FontAwesome ачаалагдаагүй ч харагдана */}
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <circle cx="12" cy="12" r="3" />
            <circle cx="12" cy="12" r="8" />
            <line x1="12" y1="2" x2="12" y2="4" />
            <line x1="12" y1="20" x2="12" y2="22" />
            <line x1="2" y1="12" x2="4" y2="12" />
            <line x1="20" y1="12" x2="22" y2="12" />
          </svg>
          <span>
            {geoStatus === "loading" ? "Авч байна..." : "GPS"}
          </span>
        </button>
      </div>

      {gps.lat != null && (
        <div
          style={{
            marginTop: 8,
            fontSize: 11,
            color: "#7A8496",
            fontFamily: "monospace",
          }}
        >
          📍 {gps.lat.toFixed(5)}, {gps.lng.toFixed(5)}
          {gps.accuracy ? ` · ±${Math.round(gps.accuracy)}m` : ""}
        </div>
      )}

      {/* Гараар оруулах байршлын дэлгэрэнгүй тайлбар */}
      <div style={{ marginTop: 12 }}>
        <label
          style={{
            fontSize: 11,
            fontWeight: 700,
            color: "#5F5E5A",
            marginBottom: 4,
            display: "block",
            letterSpacing: 0.3,
          }}
        >
          Байршлын дэлгэрэнгүй тайлбар (заавал биш)
        </label>
        <textarea
          value={locationDescription}
          onChange={(e) => onLocationDescriptionChange?.(e.target.value)}
          rows={2}
          placeholder="Жш: 3-р давхрын дамжуулагч дээгүүр, насос #4-ийн зүүн талд"
          style={{
            ...inputStyle,
            resize: "none",
            lineHeight: 1.4,
            fontSize: 13,
          }}
        />
      </div>
    </Card>
  );
}
